import React from 'react';
import styled from 'styled-components';
import carouselData from './carousel-data';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import Imgix from 'react-imgix';
import breakpoints from '@/constants/breakpoints';

import 'swiper/swiper-bundle.min.css';
import RoundedArrow from '../../common/RoundedArrow';

const baseImgURL = 'https://theinside.imgix.net/homepage/rebrand';

const carouselBreakpoints = {
  [breakpoints['tablet']]: {
    slidesPerGroup: 1,
    slidesPerView: 2.8,
    slidesOffsetBefore: 17
  },
  [breakpoints['desktop']]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    slidesOffsetBefore: 0
  }
};

function DesignerFavorites() {
  return (
    <Container>
      <Header>
        <Title>Designer Favorites</Title>
        <ShopAll href="/collections/bestsellers">Shop All</ShopAll>
      </Header>

      <div className="slider-wrapper">
        <Swiper
          spaceBetween={13}
          slidesPerView={1.9}
          slidesOffsetBefore={17}
          slidesPerGroup={1}
          simulateTouch={false}
          className="category-slider"
          navigation={{
            nextEl: `${NextButton}`,
            prevEl: `${PrevButton}`
          }}
          breakpoints={carouselBreakpoints}
          modules={[Navigation]}
        >
          {carouselData.map(
            ({ name, sublabel, image, url, isBestSeller, fullWidth, isNew, customImgixParams }) => (
              <SwiperSlide key={name}>
                <a className="category-slide" href={url}>
                  {isBestSeller && (
                    <BestSellerStamp>
                      BEST
                      <br />
                      SELLER
                    </BestSellerStamp>
                  )}
                  {isNew &&
                    <BundleBadge>
                      <Imgix
                        className="badge-image"
                        imgixParams={{ auto: 'compress,format' }}
                        sizes="80px"
                        src={`https://theinside.imgix.net/common/new-new-badge.svg`}
                        htmlAttributes={{
                          alt: 'new product badge',
                          loading: 'lazy'
                        }}
                      />
                    </BundleBadge>
                  }
                  <SlideContent>
                    <Imgix
                      imgixParams={{
                        auto: 'compress,format',
                        fit: 'crop',
                        ar: '1:0.92',
                        crop: fullWidth ? 'bottom' : 'edges',
                        ...customImgixParams
                      }}
                      className='slide-img'
                      src={`${baseImgURL}/${image}`}
                      htmlAttributes={{
                        alt: name,
                        loading: 'lazy'
                      }}
                    />
                    <Copy>
                      <p>{name}</p>
                      <Small>{sublabel}</Small>
                    </Copy>
                  </SlideContent>
                </a>
              </SwiperSlide>
            )
          )}
        </Swiper>

        <NavButtons>
          <PrevButton>
            <RoundedArrow direction="left" />
          </PrevButton>
          <NextButton>
            <RoundedArrow />
          </NextButton>
        </NavButtons>
      </div>
    </Container>
  );
}

const Container = styled.div`
  padding: 38px 0 48px;
  margin: 0 auto;
  max-width: 1620px;

  @media (min-width: ${breakpoints['desktop']}px) {
    width: 82%;
  }

  .slider-wrapper {
    position: relative;
    margin: 0 auto;
    width: 100%;
  }

  .category-slider {
    a:hover {
      text-decoration: none;
    }

    .slide-img {
      width: 100%;
      margin-bottom: 27px;
    }
  }

  .category-slide {
    background: #f8f9fb;
    text-align: center;
    display: block;
    padding: 0 0 15px;
    margin-top: 5px;
    margin-left: 5px;
    min-height: 273px;

    p {
      margin: 0;
      font-family: ${({ theme }) => theme.fonts.secondary.family};
      font-size: 16px;
      font-weight: 300;
      color: ${props => props.theme.colors.charcoal};
    }
  }

  @media (min-width: ${breakpoints['tablet']}px) {
    padding: 47px 0 57px;

    .category-slide {
      margin-top: 7px;
      margin-left: 7px;
    }
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    padding: 56px 0 66px;

    .category-slide {
      margin-top: 10px;
      margin-left: 10px;
    }

    .category-slide p {
      font-size: 20px;
    }
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    padding: 64px 0 74px;

    .category-slide p {
      font-size: 24px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  margin-left: 21px;
  margin-bottom: 18.5px;

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-bottom: 24.6px;
    margin-left: 0;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-size: 24px;
  font-weight: 300;
  letter-spacing: -0.48px;
  color: ${props => props.theme.colors.charcoal};
  margin-right: 8.5px;
  margin-bottom: 0;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-size: 28px;
    letter-spacing: -0.56px;
    margin-right: 10.6px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 38px;
    letter-spacing: -0.76px;
    margin-right: 11.4px;
  }

  @media (min-width: ${breakpoints['medium-desktop']}px) {
    font-size: 45px;
    letter-spacing: -1.5px;
    margin-right: 18.2px;
  }
`;

const ShopAll = styled.a`
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: #9F6A41;
  font-size: 16px;
  text-decoration: underline;
  margin-bottom: 10px;

  &:hover {
    text-decoration: none;
    color: #9F6A41;
  }

  media (min-width: ${breakpoints['tablet']}px) {
    font-size: 16px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 20px;
  }
`;

const SlideContent = styled.div`
  display: block;
`;

const Copy = styled.div`
  display: block;
  text-align: center;
  padding: 0 15px;
`;

const Small = styled.small`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-size: 9px;
  font-weight: 300;
  color: ${props => props.theme.colors.charcoal};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-size: 12px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 14px;
  }
`;

const BestSellerStamp = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  border-radius: 100%;
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.mist};
  color: ${props => props.theme.colors.charcoal};
  font-size: 8px;
  line-height: 1.13;
  letter-spacing: 0.8px;

  @media (min-width: ${breakpoints['tablet']}px) {
    width: 64px;
    height: 64px;
    font-size: 10px;
    line-height: 1.2;
    letter-spacing: 1px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    width: 76px;
    height: 76px;
    font-size: 12px;
    line-height: 1.2;
    letter-spacing: 1px;
  }
`;

const NavButtons = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  width: 100%;

  @media (min-width: ${breakpoints['desktop']}px) {
    display: block;
  }

  a {
    margin: 0;
    cursor: pointer;
    position: absolute;
    z-index: 1;
  }

  a:nth-child(1) {
    left: -75px;
  }
  a:nth-child(2) {
    right: -75px;
  }

  .swiper-button-disabled {
    display: none;
  }
`;

const PrevButton = styled.a``;
const NextButton = styled.a``;

const BundleBadge = styled.div`
  position: absolute;
  top: 0px;
  left: -1px;
  z-index: 3;

  .badge-image {
    width: 50px;
    height: auto;
    @media (min-width: ${breakpoints['desktop']}px) {
      width: 80px;
    }
  }
`;

export default DesignerFavorites;
